import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Check,
  ChevronDown,
  TrendingUp,
  ShieldCheck,
  CalendarRange,
  ClipboardList,
  Wifi,
  BarChart3,
  Building2,
  Layers,
  CreditCard,
  MonitorSmartphone,
  Settings,
  DollarSign,
  Users,
} from "lucide-react";
import "./HMS42003.scss";

const GROWTH_CARDS = [
  {
    icon: Settings,
    title: "Grow Your Operations",
    body: "Full-featured cloud PMS, multi-property management, powerful automation, and an intuitive user interface.",
  },
  {
    icon: Layers,
    title: "Grow Your Tech Stack",
    body: "Rely on a speedy and secure system with 100% system uptime. Seamless integration with over 1400 interfaces at no extra cost.",
  },
  {
    icon: DollarSign,
    title: "Grow Your Revenue",
    body: "Maximize room revenue with attribute-based upsells and drive automated upsells for services and packages.",
  },
  {
    icon: Building2,
    title: "Grow Your Portfolio",
    body: "Effortlessly oversee your entire chain from one dashboard, simplifying management and seamless property deployment.",
  },
  {
    icon: TrendingUp,
    title: "Grow Your Profits",
    body: "Reduce costs and increase ROI by eliminating hardware, slashing onboarding and training fees, and avoiding extra fees for integrations.",
  },
  {
    icon: Users,
    title: "Grow Your GSS Scores",
    body: "Enhance guest satisfaction with seamless digital check-in and check-out experiences via kiosk and mobile technology.",
  },
];

const PRODUCT_SUITE = [
  {
    label: "PROPERTY MANAGEMENT SYSTEM",
    icon: Building2,
    title: "Stayntouch PMS",
    body: "Manage your hotel or portfolio with a full-featured and intuitive cloud PMS. Streamline operations, guest engagement, and revenue optimization.",
  },
  {
    label: "PAYMENT SOLUTION",
    icon: CreditCard,
    title: "Stayntouch Pay",
    body: "Effortlessly manage payment transactions with our secure, end-to-end platform, ensuring simplicity and transparency without hidden fees.",
  },
  {
    label: "BOOKING ENGINE",
    icon: CalendarRange,
    title: "Booking Engine",
    body: "Ensure seamless booking experiences for guests, driving direct hotel bookings with intuitive and effortless processes.",
  },
  {
    label: "CHANNEL MANAGER",
    icon: Layers,
    title: "Channel Manager",
    body: "Gain access to leading OTAs, tap into new markets, boost revenue, and refine your distribution strategy with our comprehensive channel manager.",
  },
  {
    label: "KIOSK CHECK-IN",
    icon: MonitorSmartphone,
    title: "Stayntouch Kiosk",
    body: "Craft a unique check-in experience tailored to your brand and guest convenience, utilizing our user-friendly customizable guest kiosk.",
  },
];

const FEATURES = [
  {
    icon: CalendarRange,
    photo: "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800&q=80&auto=format&fit=crop",
    title: "One calendar for every room, every channel",
    body: "Reservations, walk-ins, and OTA bookings land in a single drag-and-drop calendar, with rates and availability synced across every channel automatically.",
  },
  {
    icon: ClipboardList,
    photo: "https://images.unsplash.com/photo-1591088398332-8a7791972843?w=800&q=80&auto=format&fit=crop",
    title: "Housekeeping that runs itself",
    body: "Room status updates the moment housekeeping finishes a clean, maintenance tickets route to the right team, and front desk always knows what’s ready to sell.",
  },
  {
    icon: BarChart3,
    photo: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=800&q=80&auto=format&fit=crop",
    title: "Revenue and occupancy, in real time",
    body: "ADR, RevPAR, and occupancy refresh as bookings come in, so rate decisions are based on what’s happening today, not last week’s export.",
  },
];

const STORIES = [
  {
    company: "Marlowe & Voss",
    industry: "Boutique Hotel Group",
    metric: "RevPAR up 21% in one season",
    quote: "Rate changes used to mean updating five different OTA extranets by hand. Now it’s one screen, and every channel updates instantly.",
    person: "General Manager",
  },
  {
    company: "Aldergate Lodge & Resort",
    industry: "Resort",
    metric: "Housekeeping turnaround cut by 40%",
    quote: "Front desk knows the second a room is clean instead of calling housekeeping to check. Guests get into rooms faster and staff stop chasing each other.",
    person: "Director of Operations",
  },
  {
    company: "Brightwell Collection",
    industry: "Multi-Property Group",
    metric: "9 properties on one dashboard",
    quote: "We run nine independent properties under one brand. Central reservations and consolidated reporting replaced a spreadsheet four people maintained by hand.",
    person: "VP of Operations",
  },
];

const FAQS = [
  {
    q: "Can it sync with the OTAs we’re already on?",
    a: "Yes. The channel manager syncs rates, availability, and bookings in real time with major OTAs including Booking.com, Expedia, and Airbnb, plus your own direct booking site.",
  },
  {
    q: "Does it work for multi-property groups?",
    a: "Yes. Growth and Enterprise plans support central reservations and a consolidated dashboard across properties, with per-property reporting and shared guest profiles.",
  },
  {
    q: "What does onboarding look like?",
    a: "Most properties go live in under two days with guided self-serve setup and data import from your previous PMS. Larger groups get a dedicated implementation manager.",
  },
  {
    q: "Is guest and payment data secure?",
    a: "Data is encrypted in transit and at rest, with PCI DSS compliant payment processing. Enterprise plans add SSO, role-based access, and dedicated infrastructure.",
  },
];

const HMS42003: React.FC = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  // Every demo / signup CTA on this page routes here.
  const goToSignup = () => navigate("/auth/signup");

  return (
    <div className="hms-wrap">
      {/* Floating Book a Demo */}
      <button className="floating-demo" onClick={goToSignup}>
        <CalendarRange size={18} />
        <span>Book a demo</span>
      </button>

      {/* ========== HERO ========== */}
      <section
        className="hms-hero-section"
        style={{
          backgroundImage:
            "linear-gradient(160deg, rgba(9,20,37,0.92) 0%, rgba(14,27,49,0.85) 40%, rgba(17,32,57,0.82) 70%, rgba(9,19,35,0.93) 100%), url('https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=1800&q=80&auto=format&fit=crop')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundColor: "#0a1628",
        }}
      >
        {/* decorative background layers */}
        <div className="hero-bg" aria-hidden="true">
          <div className="hero-grid" />
          <div className="hero-glow hero-glow-orange" />
          <div className="hero-glow hero-glow-blue" />
          <div className="hero-vignette" />
        </div>

        <div className="hero-inner">
          <div className="hero-left">
            <h1 style={{ color: "#ffffff" }}>
              Power Your Operations with a<br />
              Full-Featured Hotel Property<br />
              Management System
            </h1>

            <p className="hero-sub" style={{ color: "#a8b4c8" }}>
              Unlock unparalleled flexibility with our intuitive and cloud-native
              hotel PMS, transforming operations and enhancing guest experiences
              across your portfolio.
            </p>

            <div className="hero-buttons">
              <button className="btn-orange" onClick={goToSignup}>
                BOOK DEMO
              </button>
             
            </div>
          </div>

          <div className="hero-right">
            <div className="dashboard-card card-1">
              <div className="card-title">Work Management</div>
              <div className="card-grid">
                <div>112</div>
                <div>102</div>
                <div>95</div>
                <div>88</div>
                <div>76</div>
                <div>64</div>
              </div>
            </div>
            <div className="dashboard-card card-2">
              <div className="card-title">Guest Experience</div>
              <div className="bars">
                <div className="bar" style={{ width: "85%" }} />
                <div className="bar" style={{ width: "65%" }} />
                <div className="bar" style={{ width: "92%" }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== GROWTH ========== */}
      <section className="section growth">
        <h2 className="center-title">Check-in to Limitless Growth</h2>
        <div className="growth-grid">
          {GROWTH_CARDS.map((c) => (
            <div className="growth-card" key={c.title}>
              <div className="g-icon"><c.icon size={22} /></div>
              <h3>{c.title}</h3>
              <p>{c.body}</p>
            </div>
          ))}
        </div>
        <div className="center">
          <button className="btn-orange" onClick={goToSignup}>SCHEDULE DEMO</button>
        </div>
      </section>

      {/* ========== PRODUCT SUITE ========== */}
      <section className="section suite">
        <h2 className="center-title light">Dive into Our Complete Cloud Hotel Management Software Suite</h2>
        <div className="suite-grid">
          {PRODUCT_SUITE.map((p) => (
            <div className="suite-card" key={p.title}>
              <div className="suite-label">{p.label}</div>
              <div className="s-icon"><p.icon size={20} /></div>
              <h3>{p.title}</h3>
              <p>{p.body}</p>
              <button className="link-btn" onClick={goToSignup}>Read More →</button>
            </div>
          ))}
        </div>
      </section>

      {/* ========== FEATURES ========== */}
      <section className="section">
        <div className="center-head">
          <span className="eyebrow">Platform</span>
          <h2>Everything a property needs to run smoothly, in one place</h2>
        </div>
        <div className="feature-grid">
          {FEATURES.map((f) => (
            <div className="feature-card" key={f.title}>
              <div className="photo-wrap">
                <img src={f.photo} alt="" />
                <div className="f-icon"><f.icon size={18} /></div>
              </div>
              <h3>{f.title}</h3>
              <p>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ========== EMPOWER ========== */}
      <section className="section empower">
        <div className="empower-grid">
          <div className="empower-visual">
            <img
              src="https://images.unsplash.com/photo-1618773928121-c32242e63f39?w=900&q=80&auto=format&fit=crop"
              alt="Hotel interior"
            />
            <div className="stat-badge">
              <span>MANAGE PROPERTIES</span>
              <strong>70%</strong>
              <span>FASTER</span>
            </div>
            <div className="status-panel">
              <div><span className="dot green" /> ARRIVALS <b>31</b></div>
              <div><span className="dot red" /> DEPARTURES <b>0</b></div>
              <div><span className="dot blue" /> STAYS DIRTY <b>28</b></div>
              <div><span className="dot orange" /> DIRTY <b>31</b></div>
              <div><span className="dot yellow" /> PICKUP <b>0</b></div>
            </div>
          </div>
          <div className="empower-text">
            <h2>Empowering Owners & Operators</h2>
            <p>
              Stayntouch fuels success for owners and operators with a comprehensive
              hotel PMS that slashes training costs, drives efficiency, and eliminates
              integration expenses. Enjoy increased revenue and profitability while
              enabling leaner staff operations for enhanced operational agility and success.
            </p>
            <ul>
              <li><Check size={16} /> 100% system uptime with no maintenance or data security fees.</li>
              <li><Check size={16} /> Regular upgrades and unlimited integrations at no additional cost.</li>
              <li><Check size={16} /> Cut costs by training staff in as little as 2 days.</li>
            </ul>
            <button className="btn-outline-orange" onClick={goToSignup}>BOOK DEMO</button>
          </div>
        </div>
      </section>

      {/* ========== STORIES ========== */}
      <section className="section">
        <div className="center-head">
          <span className="eyebrow">Customer stories</span>
          <h2>Results from properties running on the platform</h2>
        </div>
        <div className="story-grid">
          {STORIES.map((s) => (
            <div className="story-card" key={s.company}>
              <div className="metric"><TrendingUp size={15} /> {s.metric}</div>
              <p className="quote">“{s.quote}”</p>
              <div className="attrib">
                <strong>{s.company}</strong>
                <span>{s.person} · {s.industry}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ========== PERFORMANCE ========== */}
      <section className="performance">
        <div className="perf-inner">
          <div className="perf-left">
            <h2>
              <span className="orange">Modern Technology,</span><br />
              Unmatched Performance.
            </h2>
            <button className="btn-orange" onClick={goToSignup}>SCHEDULE DEMO</button>
          </div>
          <div className="perf-right">
            <div className="perf-row">
              <span className="big">100%</span>
              <span className="label">Reliable System Uptime</span>
            </div>
            <div className="perf-row">
              <span className="big">96%</span>
              <span className="label">Customer Retention Rate</span>
            </div>
            <div className="perf-row">
              <span className="big">#1</span>
              <span className="label">TravelTech Hotel PMS Company of the Year</span>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SECURITY ========== */}
      <section className="security">
        <div><ShieldCheck size={18} /> PCI DSS compliant payments</div>
        <div><Wifi size={18} /> Real-time OTA & channel sync</div>
        <div><BarChart3 size={18} /> 99.95% uptime SLA</div>
      </section>

      {/* ========== FAQ ========== */}
      <section className="section">
        <div className="center-head">
          <span className="eyebrow">Questions</span>
          <h2>Frequently asked questions</h2>
        </div>
        <div className="faq-list">
          {FAQS.map((item, i) => (
            <div className="faq-item" key={item.q}>
              <button
                className="faq-q"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                {item.q}
                <ChevronDown size={18} className={openFaq === i ? "open" : ""} />
              </button>
              {openFaq === i && <p className="faq-a">{item.a}</p>}
            </div>
          ))}
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="final-cta">
        <h2>See your property running on the platform</h2>
        <p>Setup takes about fifteen minutes. No credit card required.</p>
        <div className="hero-buttons center">
          <button className="btn-orange" onClick={goToSignup}>
            Book a demo <ArrowRight size={16} />
          </button>
          <button className="btn-ghost" onClick={goToSignup}>
            Start free trial
          </button>
        </div>
      </section>
    </div>
  );
};

export default HMS42003;